import type { FC } from 'react'

import type { SxProps, Theme } from '@mui/material'
import { Box, SvgIcon, Typography } from '@mui/material'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import ChangeHistoryIcon from '@mui/icons-material/ChangeHistory'
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank'
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked'
import ReplyIcon from '@mui/icons-material/Reply'
import SquareOutlined from '@mui/icons-material/SquareOutlined'

import { HOME_CONTENT } from '../defaults'
import {
  cardSectionContainerSx,
  cardSectionTitleSx,
  cardSectionSubtitleSx,
  cardSectionCardsContainerSx,
} from '../styles'

type MuiIcon = typeof SvgIcon

export const FeatureHighlightsSection: FC = () => (
  <Box component="section" sx={cardSectionContainerSx}>
    <Typography component="h2" sx={cardSectionTitleSx}>
      {HOME_CONTENT.featureHighlights.title}
    </Typography>
    <Typography sx={cardSectionSubtitleSx}>{HOME_CONTENT.featureHighlights.description}</Typography>

    <Box sx={cardSectionCardsContainerSx(1200)}>
      <SignalCard />
      <ModeCard />
      <BeatCard />
    </Box>
  </Box>
)

const SignalCard: FC = () => (
  <Box sx={cardSx}>
    <Typography sx={cardTitleSx}>{HOME_CONTENT.eventIcons.title}</Typography>
    <Typography sx={cardSubtitleSx}>{HOME_CONTENT.eventIcons.description}</Typography>
    <Box sx={iconsRowSx}>
      {SIGNAL_ICONS.map(({ Icon, iconSx, ariaLabel, animationDelay }) => (
        <Box key={ariaLabel} sx={buildPulseWrapperSx(animationDelay)}>
          <Icon sx={iconSx} aria-hidden="true" />
        </Box>
      ))}
    </Box>
  </Box>
)

const ModeCard: FC = () => (
  <Box sx={cardSx}>
    <Typography sx={cardTitleSx}>{HOME_CONTENT.agentMode.title}</Typography>
    <Typography sx={cardSubtitleSx}>{HOME_CONTENT.agentMode.description}</Typography>
    <Box
      sx={iconContainerSx}
      role="img"
      aria-label="Agent mode indicator cycling through permission levels"
    >
      <SquareOutlined sx={diamondSx} aria-hidden="true" />
    </Box>
  </Box>
)

const BeatCard: FC = () => (
  <Box sx={cardSx}>
    <Typography sx={cardTitleSx}>{HOME_CONTENT.eventNotifications.title}</Typography>
    <Typography sx={cardSubtitleSx}>{HOME_CONTENT.eventNotifications.description}</Typography>
    <Box sx={iconsRowSx}>
      {BEAT_ICONS.map(({ Icon, iconSx, ariaLabel, animationDelay }) => {
        const wrapperSx =
          animationDelay === undefined ? staticWrapperSx : buildPulseWrapperSx(animationDelay)
        return (
          <Box key={ariaLabel} sx={wrapperSx}>
            <Icon sx={iconSx} aria-hidden="true" />
          </Box>
        )
      })}
    </Box>
  </Box>
)

const PLAN_APPROVAL_COLOR = '#00acc1'

interface SignalIcon {
  readonly Icon: MuiIcon
  readonly animationDelay: string
  readonly iconSx: SxProps<Theme>
  readonly ariaLabel: string
}

const SIGNAL_ICONS: readonly SignalIcon[] = [
  {
    Icon: RadioButtonUncheckedIcon,
    animationDelay: '0s',
    iconSx: { fontSize: 48, color: 'warning.main' },
    ariaLabel: 'Question event',
  },
  {
    Icon: CheckBoxOutlineBlankIcon,
    animationDelay: '0.6s',
    iconSx: { fontSize: 48, color: PLAN_APPROVAL_COLOR },
    ariaLabel: 'Plan approval event',
  },
  {
    Icon: ChangeHistoryIcon,
    animationDelay: '1.2s',
    iconSx: { fontSize: 48, color: 'warning.main' },
    ariaLabel: 'Tool permission event',
  },
]

interface BeatIcon {
  readonly Icon: MuiIcon
  readonly iconSx: SxProps<Theme>
  readonly ariaLabel: string
  readonly animationDelay?: string
}

const BEAT_ICONS: readonly BeatIcon[] = [
  {
    Icon: ArrowForwardIcon,
    iconSx: { fontSize: 48, color: 'secondary.main', opacity: 0.7 },
    ariaLabel: 'File modification event',
  },
  {
    Icon: ReplyIcon,
    iconSx: { fontSize: 56, color: 'success.main' },
    ariaLabel: 'Reply event',
    animationDelay: '0.6s',
  },
  {
    Icon: ArrowForwardIcon,
    iconSx: { fontSize: 48, color: 'primary.light', opacity: 0.7 },
    ariaLabel: 'User prompt event',
  },
]

const cardSx: SxProps<Theme> = (theme) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  textAlign: 'center',
  flex: { xs: '1 1 100%', md: '1 1 0' },
  maxWidth: 420,
  px: { xs: 2.5, md: 4 },
  py: { xs: 3.5, md: 5 },
  borderRadius: 2,
  backgroundColor:
    theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.03)' : 'rgba(62, 39, 35, 0.03)',
  border:
    theme.palette.mode === 'dark'
      ? '1px solid rgba(255, 255, 255, 0.06)'
      : '1px solid rgba(62, 39, 35, 0.08)',
})

const cardTitleSx: SxProps<Theme> = {
  color: 'text.primary',
  fontSize: { xs: '1.25rem', md: '1.5rem' },
  fontWeight: 600,
  mb: 0.5,
}

const cardSubtitleSx: SxProps<Theme> = {
  color: 'text.secondary',
  fontSize: '0.95rem',
  lineHeight: 1.6,
}

const iconsRowSx: SxProps<Theme> = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  gap: 3,
  mt: 3,
}

const iconContainerSx: SxProps<Theme> = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  height: 48,
  mt: 3,
}

const diamondSx: SxProps<Theme> = (theme) => ({
  fontSize: 48,
  transform: 'rotate(45deg)',
  color: theme.palette.mode === 'dark' ? '#f5f5f5' : '#6D4C41',
  '@media (prefers-reduced-motion: no-preference)': {
    '@keyframes modeColorCycleFeature': {
      '0%, 100%': { color: theme.palette.mode === 'dark' ? '#f5f5f5' : '#6D4C41' },
      '25%': { color: '#00acc1' },
      '50%': { color: '#c62828' },
      '75%': { color: '#ba68c8' },
    },
    animation: 'modeColorCycleFeature 8s ease-in-out infinite',
  },
})

const staticWrapperSx: SxProps<Theme> = { display: 'flex' }

const buildPulseWrapperSx = (delay: string): SxProps<Theme> => ({
  display: 'flex',
  '@media (prefers-reduced-motion: no-preference)': {
    '@keyframes pulse': {
      '0%, 100%': { transform: 'scale(1)', opacity: 0.85 },
      '50%': { transform: 'scale(1.12)', opacity: 1 },
    },
    animation: `pulse 2.5s ease-in-out ${delay} infinite`,
  },
})
